import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { setColor } from "../../../assets/styles/styles";
import LandingPageContainer from "../components/LandingPageContainer";
import HeroContainer from "../components/HeroContainer";
import VideoSection from "./components/VideoSection";
import GallerySection from "./components/GallerySection";
import FeatureSection from "./components/FeatureSection";
import NewsSection from "./components/NewsSection";
import ReviewSection from "./components/ReviewSection";
import ButtonSection from "./components/ButtonSection";
import ExploreSection from "./components/ExploreSection";
import Footer from "../components/FooterSection";
import HallwayStyles from "../../../features/hallway/HallwayStyles";
import LandingHeader from "../intraverseLandingPage/LandingHeader";
import Pro from "../pro/Pro";

const Live = () => {
    const widgetRef = useRef(null);
    const wrapperRef = useRef(null);
    const navigate = useNavigate();
    const { pathname, search, hash } = useLocation();

    const [explorePage, setExplorePage] = useState(false);
    const [isPro, setIsPro] = useState(false);
    const [isAbout, setIsAbout] = useState(true);

    useEffect(() => {
        const wrapper = widgetRef.current;
        if (!wrapper) return;

        const script = document.createElement("script");
        script.src = "https://hallway.ai/embed-loader.js";
        script.async = true;
        wrapper.appendChild(script);

        return () => {
            wrapper.removeChild(script);
        };
    }, []);

    useEffect(() => {
        const wrapper = widgetRef.current;
        if (!wrapper) return;

        const onNavigate = (e) => {
            const { url, openInNewTab } = e.detail;
            if (openInNewTab) {
                window.open(url, "_blank");
                return;
            }

            let hashPart = "";
            try {
                hashPart = new URL(url).hash;
            } catch {
                hashPart = url.startsWith("#") ? url : `#${url}`;
            }

            const target = `${pathname}${search}${hashPart}`;
            navigate(target, { replace: true });
        };

        wrapper.addEventListener("navigate", onNavigate);
        return () => wrapper.removeEventListener("navigate", onNavigate);
    }, [navigate, pathname, search]);

    useEffect(() => {
        const wrapper = widgetRef.current;
        if (wrapper && typeof wrapper.navigate === "function") {
            wrapper.navigate(`${pathname}${search}${hash}`);
        }
    }, [pathname, search, hash]);

    // 3) Toggle Pro/Explore flags based on the current hash
    useEffect(() => {
        if (hash.startsWith("#pro")) {
            setIsPro(true);
            setExplorePage(false);
            setIsAbout(false);
        } else if (hash.startsWith("#explore")) {
            setExplorePage(true);
            setIsPro(false);
            setIsAbout(false);
        } else {
            setIsPro(false);
            setExplorePage(false);
            setIsAbout(true);
        }
    }, [hash, setIsPro, setExplorePage]);

    // 4) Smooth–scroll **before paint** to the element matching the hash
    useLayoutEffect(() => {
        if (!hash) return;
        const id = hash.slice(1);
        requestAnimationFrame(() => {
            const targetEl = document.getElementById(id);
            if (targetEl) {
                targetEl.scrollIntoView({ behavior: "smooth" });
            }
        });
    }, [hash]);

    return (
        <>
            <LandingHeader
                explorePage={explorePage}
                setExplorePage={setExplorePage}
                isPro={isPro}
                setIsPro={setIsPro}
                isAbout={isAbout}
                setIsAbout={setIsAbout}
            />
            <HallwayStyles />
            {/* Hallway Embed Widget */}
            {/* IMPORTANT: className="hallway-instance" makes your existing global ::part styles apply */}
            <WidgetWrapper className="hallway-instance-home" ref={wrapperRef}>
                <hallway-embed
                    ref={widgetRef}
                    character-id={
                        process.env.REACT_APP_STREAM_API_ENV === "production"
                            ? "81f177cd-04c6-4bad-9583-d13d3c46ba70"
                            : "4053b73b-29bb-4dcf-9d72-69d4f4a0cbbc"
                    }
                    theme="dark"
                    style={{
                        maxHeight: 480,
                        maxWidth: 300,
                        height: 380,
                        width: 300,
                        bottom: "0px",
                        right: "20px",
                        border: "none",
                    }}
                    data-location="bottom"
                    disable-mobile-styling="true"
                />
            </WidgetWrapper>

            <LandingPageContainer showOutline explorePage={explorePage}>
                {explorePage ? (
                    <>
                        <div id="explore">
                            <ExploreSection />
                        </div>
                        <div id="explorecta">
                            <ButtonSection
                                videoUrl="/videos/landingPages/live-footer-v3.mp4"
                                buttonText="Contact us"
                                explorePage={explorePage}
                            />
                        </div>
                        <div id="explorefooter">
                            <Footer
                                title={
                                    <>
                                        Join Our <Highlighted>Socials</Highlighted>
                                    </>
                                }
                            />
                        </div>
                    </>
                ) : (
                    <>
                        {!isPro && (
                            <>
                                <div id="hero">
                                    <HeroContainer
                                        videoUrl="/videos/landingPages/Header_live.mp4"
                                        buttonText="Sign Up Now"
                                        description="Dive Into Digital Worlds Made By Your Favorite Creators"
                                        arrowText="Join The Intraverse"
                                        fadeColor={setColor.darkPurpleBackground}
                                    />
                                </div>
                                <div id="video">
                                    <VideoSection />
                                </div>
                                <div id="experiences">
                                    <GallerySection />
                                </div>
                                <div id="features">
                                    <FeatureSection />
                                </div>
                                <div id="news">
                                    <NewsSection />
                                </div>
                                <div id="reviews">
                                    <ReviewSection />
                                </div>
                                <div id="cta">
                                    <ButtonSection
                                        videoUrl="/videos/landingPages/live-footer-v3.mp4"
                                        buttonText="Sign Up Now"
                                    />
                                </div>
                                <div id="footer">
                                    <Footer
                                        title={
                                            <>
                                                Join Our <Highlighted>Socials</Highlighted>
                                            </>
                                        }
                                    />
                                </div>
                            </>
                        )}
                        {isPro && <Pro />}
                    </>
                )}
            </LandingPageContainer>
        </>
    );
};

export default Live;

const Highlighted = styled.span`
    color: ${setColor.accent4};
`;

const WidgetWrapper = styled.div`
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 100000;

    /* Let your global mobile rules (100vw, etc) actually have space */
    @media (max-width: 768px) {
        right: 0;
        bottom: 0;
    }
`;
